import { useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { CustomerStatusHistoryResponse } from '@lio/api-types';
import { config } from '@services/config';
import { customerQueryKey } from './keys';

export type CustomerStatusHistoryEntry = CustomerStatusHistoryResponse['items'][number];

const PAGE_SIZE = 10;

/**
 * Fetch a customer's status-change history (most recent first). Enabled lazily
 * so it only runs when the View Customer Status History modal is opened. Pages
 * are loaded in batches of {@link PAGE_SIZE}; call `fetchNextPage` to append the
 * next page, and check `hasNextPage` to know when older entries remain.
 */
export const useCustomerStatusHistory = (customerId: string, enabled = true) => {
  const client = useClient();
  const result = useInfiniteQuery<CustomerStatusHistoryResponse, Error>({
    queryKey: customerQueryKey.getStatusHistory(customerId),
    queryFn: ({ pageParam = 0 }) => {
      const query = new URLSearchParams({
        pageLimit: PAGE_SIZE.toString(),
        pageOffset: String(pageParam),
      });
      return client
        .get(`customer/${customerId}/status-history?${query.toString()}`)
        .json<CustomerStatusHistoryResponse>();
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.pagination.pageOffset + lastPage.pagination.pageLimit;
      return nextOffset < lastPage.pagination.total ? nextOffset : undefined;
    },
    enabled: enabled && !!customerId && customerId !== '',
    refetchOnWindowFocus: false,
    ...config,
  });
  const entries = result.data?.pages.flatMap((page) => page.items) ?? [];
  const total = result.data?.pages[0]?.pagination.total ?? 0;
  return { ...result, entries, total };
};
